import { InboxOutlined } from '@ant-design/icons';
import {
  Col,
  DatePicker,
  Form,
  FormInstance,
  Input,
  message,
  Row,
  Select,
  Upload,
} from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { RcFile } from 'antd/lib/upload';
import NumberFilter from 'components/UI/_shared/NumberFilter';
import AutoCategory from 'model/ServiceRequests/AutoCategory';
import Category from 'model/ServiceRequests/Category';
import SubCategory from 'model/ServiceRequests/SubCategory';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { sessionSelectors } from 'store/state/selectors';
import useServiceRequestsStore, {
  IFormFilter,
} from 'zustand/serviceRequests/useServiceRequestsStore';
import * as S from './ServiceRequestFilter.styled';

export default function ServiceRequestFilter() {
  const [formFilter] = useForm<IFormFilter>();
  const [saleType, setSaleType] = useState<string>('');
  const [hasOrderProductDiscrepancy, setHasOrderProductDiscrepancy] =
    useState<string>('');
  const [physicalProductReturned, setPhysicalProductReturned] =
    useState<string>('');
  const { email } = useSelector(sessionSelectors.getRoot);

  const {
    clearAttachmentFile,
    createServiceRequest,
    setFormInstance,
    getSubCategories,
    getAutoCategories,
    sendAttachmentFile,
    isFiltersSuccessful,
    filters,
    setData,
    validateEmailAndLoadFilters,
    buttonDisabled,
  } = useServiceRequestsStore();

  useEffect(() => {
    const instance = {
      validateFields: formFilter.validateFields,
      getFieldValue: formFilter.getFieldValue,
      getFieldsValue: formFilter.getFieldsValue,
      setFieldsValue: formFilter.setFieldsValue,
    } as Partial<FormInstance<IFormFilter>>;

    setFormInstance(instance);
  }, [formFilter, setFormInstance]);

  useEffect(() => {
    if (email) {
      validateEmailAndLoadFilters(email);
    }
  }, [validateEmailAndLoadFilters, email]);

  const onSubmit = async () => {
    try {
      await formFilter.validateFields();
    } catch {
      message.error(
        'Alguns campos obrigatórios não foram preenchidos corretamente. Por favor, preencha-os e tente novamente.',
      );
      return;
    }

    try {
      const response = await createServiceRequest();

      if (response && typeof response === 'object' && 'erro' in response) {
        const errorMessage = response.erro;
        message.error(errorMessage);
        return;
      }
      formFilter.resetFields();
      clearAttachmentFile();
      message.success(
        `Solicitação criada com sucesso! Número do chamado: ${response}`,
      );
    } catch (submitError: any) {
      console.error('Erro ao enviar a solicitação:', submitError);
      const backendMessage = submitError?.response?.data?.message;
      message.error(
        backendMessage ??
          'Ocorreu um erro ao enviar a solicitação. Tente novamente mais tarde.',
      );
    }
  };

  const handleCategoryOnClear = () => {
    formFilter.setFieldsValue({
      categoryId: undefined,
    });
  };

  const handleCategoryOnChange = () => {
    formFilter.setFieldsValue({
      subCategoryId: undefined,
      autoCategoryId: undefined,
    });

    getSubCategories();
    getAutoCategories();
  };

  const handleSubgroupOnClear = () => {
    formFilter.setFieldsValue({
      subCategoryId: undefined,
    });
  };

  const handleSubgroupOnChange = () => {
    formFilter.setFieldsValue({
      autoCategoryId: undefined,
      menuOrPage: undefined,
      completeName: undefined,
      periodDate: undefined,
    });

    getAutoCategories();
  };

  const handleAutoCategoryOnClear = () => {
    formFilter.setFieldsValue({
      autoCategoryId: undefined,
      menuOrPage: undefined,
      completeName: undefined,
      periodDate: undefined,
    });
  };

  const handleAutoCategoryOnChange = (autoCategoryId: number) => {
    const autoCategoryName = filters.autoCategories.data.find(
      (item: AutoCategory) => item.account === autoCategoryId,
    )?.name;
    const failureAutoCategory = autoCategoryName?.includes('Falha') ?? false;
    const reportsAutoCategory =
      autoCategoryName?.includes('Relatório') ?? false;
    const invoiceInssuanceAutoCategory =
      autoCategoryName?.includes('Emissão Nota Fiscal') ?? false;
    const invoiceRefusedAutoCategory =
      autoCategoryName?.includes('Nota Fiscal Recusada') ?? false;
    const returnWithInvoiceAutoCategory =
      autoCategoryName?.includes('Devolução com Nota Fiscal') ?? false;

    setData((state) => {
      state.filters.autoCategories.isFailure = failureAutoCategory;
      state.filters.autoCategories.isReports = reportsAutoCategory;
      state.filters.autoCategories.isInvoiceInssuance =
        invoiceInssuanceAutoCategory;
      state.filters.autoCategories.isInvoiceRefused =
        invoiceRefusedAutoCategory;
      state.filters.autoCategories.isReturnWithInvoice =
        returnWithInvoiceAutoCategory;
    });
  };

  const handleCustomerAccountOnChange = (customerAccountCode: string) => {
    getSubCategories();
    getAutoCategories();
    const isCarrefour =
      customerAccountCode === '999' || customerAccountCode === '8399';
    const customer = (filters.userInformation.data ?? []).find(
      (customer) => customer.code === customerAccountCode,
    );
    setData((state) => {
      state.filters.userInformation.isEletronicDistribution =
        customer?.isEletronicDistribution ?? false;
      state.filters.userInformation.isCarrefour = isCarrefour;
    });
  };

  if (!filters.email.isValid) {
    return (
      <S.ErrorContainer>
        <S.Icon data-testid="error-icon" />
        <S.Title>
          Ocorreu um erro inesperado! Não foi possível localizar sua conta no
          Serviço de Suporte. Atualize a página e tente novamente, caso persista
          entre em contato com o TI Veiling.
        </S.Title>
      </S.ErrorContainer>
    );
  }

  return (
    <S.Container>
      <Form
        form={formFilter}
        initialValues={filters.formFilter}
        layout="vertical"
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="Cliente"
              name="customerAccountCode"
              rules={[
                {
                  required: true,
                  message: 'Selecione corretamente uma chave de cliente',
                },
              ]}
            >
              <Select
                showSearch
                optionFilterProp="children"
                allowClear
                loading={filters.userInformation.isLoading}
                disabled={filters.userInformation.isLoading}
                placeholder="Selecione uma chave"
                onChange={handleCustomerAccountOnChange}
              >
                {(filters.userInformation.data ?? []).map((customer) => (
                  <Select.Option key={customer.code} value={customer.code}>
                    {`${customer.code} - ${customer.name}`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col lg={12} xs={24}>
            <Form.Item
              label="Categoria"
              name="categoryId"
              rules={[
                {
                  required: true,
                  message: 'Selecione corretamente uma categoria',
                },
              ]}
            >
              <Select
                placeholder="Selecione uma categoria"
                showSearch
                showArrow
                allowClear
                loading={filters.categories.isLoading}
                disabled={filters.categories.isLoading}
                optionFilterProp="children"
                optionLabelProp="label"
                onClear={handleCategoryOnClear}
                onChange={handleCategoryOnChange}
              >
                {filters.categories.data.map((item: Category) => (
                  <Select.Option
                    key={item.account}
                    value={item.account}
                    label={item.name}
                    title={item.name}
                  >
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col lg={12} xs={24}>
            <Form.Item
              label="Sub Categoria"
              name="subCategoryId"
              rules={[
                {
                  required: true,
                  message: 'Selecione corretamente uma sub categoria',
                },
              ]}
            >
              <Select
                placeholder="Selecione uma sub categoria"
                showSearch
                showArrow
                allowClear
                loading={filters.subCategories.isLoading}
                disabled={filters.subCategories.isLoading}
                optionFilterProp="children"
                optionLabelProp="label"
                onClear={handleSubgroupOnClear}
                onChange={handleSubgroupOnChange}
              >
                {filters.subCategories.data.map((item: SubCategory) => (
                  <Select.Option
                    key={item.account}
                    value={item.account}
                    label={item.name}
                    title={item.name}
                  >
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col lg={24}>
            <Form.Item
              label="Assunto"
              name="autoCategoryId"
              rules={[
                {
                  required: true,
                  message: 'Selecione corretamente um assunto de solicitação',
                },
              ]}
            >
              <Select
                placeholder="Selecione um assunto de solicitação"
                showSearch
                showArrow
                allowClear
                loading={filters.autoCategories.isLoading}
                disabled={filters.autoCategories.isLoading}
                optionFilterProp="children"
                optionLabelProp="label"
                onClear={handleAutoCategoryOnClear}
                onChange={handleAutoCategoryOnChange}
              >
                {filters.autoCategories.data.map((item: AutoCategory) => (
                  <Select.Option
                    key={item.account}
                    value={item.account}
                    label={item.name}
                    title={item.name}
                  >
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          {filters.autoCategories.isFailure ? (
            <Col lg={24}>
              <Form.Item label="Informe o menu ou página" name="menuOrPage">
                <Input />
              </Form.Item>
            </Col>
          ) : null}
          {filters.autoCategories.isReports ? (
            <>
              <Col lg={24}>
                <Form.Item
                  label="Informe seu nome completo"
                  name="completeName"
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col lg={24}>
                <Form.Item
                  label="Informe o período inicial e final"
                  name="periodDate"
                >
                  <DatePicker.RangePicker
                    key="date-picker"
                    aria-label="Informe o período inicial e final"
                    allowClear
                    format="DD/MM/YYYY"
                  />
                </Form.Item>
              </Col>
            </>
          ) : null}
          {filters.userInformation.isEletronicDistribution &&
          filters.autoCategories.isInvoiceInssuance ? (
            <>
              <S.WarningText>
                ATENÇÃO: Recomendamos fazer toda a conferencia logística antes
                da solicitação da Nota Fiscal, evitando transtornos e
                retrabalhos na operação.
              </S.WarningText>
              <Col lg={12}>
                <Form.Item
                  label="Selecione o local de emissão da nota fiscal"
                  name="invoiceIssuanceLocation"
                  rules={[
                    {
                      required: true,
                      message: 'Selecione o local de emissão da nota fiscal',
                    },
                  ]}
                >
                  <Select placeholder="Selecione o local" allowClear>
                    <Select.Option
                      key="CVHH"
                      value="CVH HOLAMBRA"
                      label="CVH HOLAMBRA"
                      title="CVH HOLAMBRA"
                    >
                      CVH HOLAMBRA
                    </Select.Option>
                    <Select.Option
                      key="CVHUB"
                      value="CVH UBERLÂNDIA"
                      label="CVH UBERLÂNDIA"
                      title="CVH UBERLÂNDIA"
                    >
                      CVH UBERLÂNDIA
                    </Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col lg={12}>
                <Form.Item
                  label="Selecione o tipo de venda"
                  name="saleType"
                  rules={[
                    { required: true, message: 'Selecione o tipo de venda' },
                  ]}
                >
                  <Select
                    placeholder="Selecione o tipo"
                    allowClear
                    value={saleType}
                    onChange={setSaleType}
                  >
                    <Select.Option
                      key="I"
                      value="Intermediação"
                      label="Intermediação"
                      title="Intermediação"
                    >
                      Intermediação
                    </Select.Option>
                    <Select.Option
                      key="Klok/LKP"
                      value="Klok/LKP"
                      label="Klok/LKP"
                      title="Klok/LKP"
                    >
                      Klok/LKP
                    </Select.Option>
                    <Select.Option
                      key="Intermediação/Klok/LKP"
                      value="Intermediação/Klok/LKP"
                      label="Intermediação/Klok/LKP"
                      title="Intermediação/Klok/LKP"
                    >
                      Intermediação/Klok/LKP
                    </Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              {saleType === 'Intermediação' ||
              saleType === 'Intermediação/Klok/LKP' ? (
                <Col lg={24}>
                  <NumberFilter
                    required
                    label="Informe o número do pedido"
                    propertyName="orderNumber"
                  />
                </Col>
              ) : null}
              <Col lg={8}>
                <Form.Item
                  label="Todos os produtos foram recepcionados?"
                  name="allProductsReceived"
                  rules={[
                    {
                      required: true,
                      message:
                        'Selecione se todos os produtos foram recepcionados',
                    },
                  ]}
                >
                  <Select placeholder="Selecione uma opção" allowClear>
                    <Select.Option value="Sim">Sim</Select.Option>
                    <Select.Option value="Não">Não</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col lg={8}>
                <Form.Item
                  label="Todos os produtos foram entregues no box?"
                  name="allProductsDeliveredToBox"
                  rules={[
                    {
                      required: true,
                      message: 'Selecione se todos os produtos foram entregues',
                    },
                  ]}
                >
                  <Select placeholder="Selecione uma opção" allowClear>
                    <Select.Option value="Sim">Sim</Select.Option>
                    <Select.Option value="Não">Não</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col lg={8}>
                <Form.Item
                  label="Existe alguma divergência entre o pedido e os produtos entregues no box?"
                  name="hasOrderProductDiscrepancy"
                  rules={[
                    {
                      required: true,
                      message: 'Selecione se existe divergência',
                    },
                  ]}
                >
                  <Select
                    placeholder="Selecione uma opção"
                    allowClear
                    value={hasOrderProductDiscrepancy}
                    onChange={setHasOrderProductDiscrepancy}
                  >
                    <Select.Option value="Sim">Sim</Select.Option>
                    <Select.Option value="Não">Não</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              {hasOrderProductDiscrepancy === 'Sim' ? (
                <Col lg={24}>
                  <Form.Item
                    label="Informe a divergência"
                    name="discrepancyDescription"
                    rules={[
                      { required: true, message: 'Informe a divergência' },
                    ]}
                  >
                    <Input placeholder="Informe a descrição" />
                  </Form.Item>
                </Col>
              ) : null}
              <Col lg={24}>
                <Form.Item
                  label="Informe o nome do responsável"
                  name="responsibleNameWhenInvoiceIssuance"
                  rules={[
                    {
                      required: true,
                      message: 'Informe o nome do responsável',
                    },
                  ]}
                >
                  <Input placeholder="Informe o nome" />
                </Form.Item>
              </Col>
            </>
          ) : null}
          {filters.userInformation.isCarrefour &&
          filters.autoCategories.isInvoiceRefused ? (
            <>
              <Col lg={12}>
                <Form.Item
                  label="Informe a chave filial"
                  name="siteKeyWhenInvoiceRefused"
                  rules={[
                    { required: true, message: 'Informe a chave filial' },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col lg={12}>
                <NumberFilter
                  required
                  label="Informe o número da nota fiscal"
                  propertyName="invoiceNumber"
                />
              </Col>
              <Col lg={24}>
                <Form.Item
                  label="Informe o motivo da recusa"
                  name="refusalReason"
                  rules={[
                    { required: true, message: 'Informe o motivo da recusa' },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col lg={12}>
                <Form.Item
                  label="Informe a loja que será faturado"
                  name="billingStore"
                  rules={[
                    {
                      required: true,
                      message: 'Informe a loja que será faturado',
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col lg={12}>
                <Form.Item
                  label="Informe o nome do responsável"
                  name="responsibleNameWhenInvoiceRefused"
                  rules={[
                    {
                      required: true,
                      message: 'Informe o nome do responsável',
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </>
          ) : null}
          {filters.userInformation.isCarrefour &&
          filters.autoCategories.isReturnWithInvoice ? (
            <>
              <Col lg={24}>
                <S.WarningText>
                  *Anexe a nota fiscal de devolução (obrigatório)
                </S.WarningText>
              </Col>
              <Col lg={8}>
                <Form.Item
                  label="Informe a chave filial"
                  name="siteKeyWhenReturnWithInvoice"
                  rules={[
                    { required: true, message: 'Informe a chave filial' },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col lg={8}>
                <NumberFilter
                  required
                  label="Informe o número da nota fiscal de devolução do cliente"
                  propertyName="customerReturnInvoiceNumber"
                />
              </Col>
              <Col lg={8}>
                <NumberFilter
                  required
                  label="Informe o valor da nota fiscal de devolução do cliente"
                  propertyName="customerReturnInvoiceValue"
                />
              </Col>
              <Col lg={8}>
                <Form.Item
                  label="Selecione o tipo de devolução"
                  name="returnType"
                  rules={[
                    {
                      required: true,
                      message: 'Selecione o tipo de devolução',
                    },
                  ]}
                >
                  <Select placeholder="Selecione uma opção" allowClear>
                    <Select.Option value="Total">Total</Select.Option>
                    <Select.Option value="Parcial">Parcial</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col lg={8}>
                <Form.Item
                  label="Selecione o motivo da devolução"
                  name="returnReason"
                  rules={[
                    {
                      required: true,
                      message: 'Selecione o motivo da devolução',
                    },
                  ]}
                >
                  <Select placeholder="Selecione uma opção" allowClear>
                    <Select.Option value="Sem sortimento">
                      Sem sortimento
                    </Select.Option>
                    <Select.Option value="Sem pedido">Sem pedido</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col lg={8}>
                <Form.Item
                  label="O produto físico foi devolvido?"
                  name="physicalProductReturned"
                  rules={[
                    {
                      required: true,
                      message: 'Selecione se o produto físico foi devolvido',
                    },
                  ]}
                >
                  <Select
                    placeholder="Selecione uma opção"
                    allowClear
                    value={physicalProductReturned}
                    onChange={setPhysicalProductReturned}
                  >
                    <Select.Option value="Sim">Sim</Select.Option>
                    <Select.Option value="Não">Não</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              {physicalProductReturned === 'Sim' ? (
                <Col lg={24}>
                  <Form.Item
                    label="Informe o local da devolução"
                    name="returnLocation"
                    rules={[
                      {
                        required: true,
                        message: 'Informe o local da devolução',
                      },
                    ]}
                  >
                    <Input placeholder="Informe o local" allowClear />
                  </Form.Item>
                </Col>
              ) : null}
              <Col lg={6}>
                <Form.Item
                  label="Informe a loja que será emitida a nota fiscal"
                  name="invoiceIssuingStore"
                  rules={[
                    {
                      required: true,
                      message: 'Informe a loja que será emitida a nota fiscal',
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col lg={6}>
                <Form.Item
                  label="Informe o nome do responsável - Operador Logístico"
                  name="logisticOperatorResponsibleName"
                  rules={[
                    {
                      required: true,
                      message:
                        'Informe o nome do responsável - Operador Logístico',
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col lg={6}>
                <NumberFilter
                  required
                  label="Informe o número da nota fiscal da CVH"
                  propertyName="cvhInvoiceNumber"
                />
              </Col>
              <Col lg={6}>
                <NumberFilter
                  required
                  label="Informe o valor da nota fiscal da CVH"
                  propertyName="cvhInvoiceValue"
                />
              </Col>
            </>
          ) : null}
          <Col lg={24}>
            <Form.Item
              label="Descrição"
              name="description"
              rules={[{ required: true, message: 'Descreva sua solicitação' }]}
            >
              <S.TextAreaDescription />
            </Form.Item>
          </Col>
          <Col lg={24}>
            <Form.Item
              label="Anexos"
              name="attachments"
              rules={[
                () => ({
                  validator() {
                    if (
                      (filters.autoCategories.isFailure ||
                        (filters.userInformation.isEletronicDistribution &&
                          filters.autoCategories.isInvoiceInssuance)) &&
                      (!filters.attachments.data ||
                        filters.attachments.data.length < 1)
                    ) {
                      return Promise.reject(
                        new Error('Anexe pelo menos 1 arquivo'),
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <>
                {filters.autoCategories.isFailure &&
                filters.attachments.data.length <= 0 ? (
                  <S.WarningText>
                    Todo chamado de falha deve conter um anexo relacionado ao
                    problema, favor inserir e prosseguir com a abertura de
                    chamado
                  </S.WarningText>
                ) : null}
                <Upload.Dragger
                  accept=".png, .jpg, .jpeg, .jfif, .pdf, .txt"
                  name="attachment"
                  fileList={filters.attachments.data}
                  multiple
                  beforeUpload={(file: RcFile) => {
                    const isLt4M = file.size / 1024 / 1024 < 4;
                    if (!isLt4M) {
                      message.error('O Arquivo deve ser menor que 4MB!');
                    }
                    return isLt4M;
                  }}
                  onChange={(info) => sendAttachmentFile(info)}
                  customRequest={(options: any) => {
                    const { onSuccess } = options;
                    onSuccess('Ok');
                  }}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined style={{ color: '#F78E1E' }} />
                  </p>
                  <p className="ant-upload-text">
                    Arraste seu arquivo ou clique para upload
                  </p>
                  <p className="ant-upload-hint">
                    Formatos de arquivo suportados: JPG / JPEG / PDF / PNG / TXT
                  </p>
                </Upload.Dragger>
              </>
            </Form.Item>
          </Col>
          <Col lg={24} xs={12}>
            <S.Button
              disabled={
                !isFiltersSuccessful || !filters.email.isValid || buttonDisabled
              }
              loading={buttonDisabled}
              onClick={onSubmit}
              type="primary"
              htmlType="submit"
              aria-label="search"
              size="large"
            >
              Enviar
            </S.Button>
          </Col>
        </Row>
      </Form>
    </S.Container>
  );
}
